/**
 * i18n do widget: bundle vem do endpoint público `/v1/public/i18n/embed`
 * (memory `feedback_oma_erros_via_backend`). Fallback PT-BR hardcoded é
 * apenas pra primeiro paint offline / falha de rede no boot — não deve ser
 * usado em produção depois do bundle chegar.
 *
 * Live aqui (não dentro de `App.tsx`) pra evitar circular import com os 4
 * step components da calc-organização.
 */

export const I18N_FALLBACK_PTBR: Record<string, string> = {
  'errors.fuel_required': 'Informe o combustível.',
  'errors.email_required': 'Informe um e-mail válido para receber o relatório.',
  'errors.consent_required': 'Você precisa concordar para receber o relatório.',
  'errors.event_days_min': 'Informe os dias de evento (mínimo 1).',
  'errors.distance_required': 'Informe a distância em km.',
  'errors.validation': 'Confira os dados informados e tente novamente.',
  'errors.network_error': 'Não foi possível carregar a calculadora.',
  'errors.unavailable': 'Esta calculadora não está disponível no momento.',
  'errors.stock_insufficient': 'Acima do estoque disponível.',
  'errors.purchase_disabled': 'Esta calculadora não suporta compra direta no momento.',
  consent_label:
    'Quero receber meu relatório por e-mail e novidades da OMA Ativos Ambientais. Posso me descadastrar a qualquer momento.',
  privacy_link: 'Política de privacidade',

  // Fluxo organização (mockup 20). Mantém em sincronia com bundleKeys em
  // leads_i18n_handler.go e com a árvore calculator.org.* em translations.json.
  'org.common.next': 'Próximo →',
  'org.common.back': '← Anterior',
  'org.common.yes': 'Sim',
  'org.common.no': 'Não',
  'org.units.people': 'pessoas',
  'org.units.km': 'km',
  'org.units.hours': 'horas',
  'org.units.kg': 'kg',
  'org.title.step1': 'Equipe, público e eletricidade',
  'org.title.step2': 'Gastos com deslocamento',
  'org.title.step3': 'Outros consumos',
  'org.title.step4': 'Informações do evento',
  'org.step1.title': 'Equipe do evento e público',
  'org.step1.crew': 'Quantas pessoas trabalharão em seu evento?',
  'org.step1.capacity': 'Qual a lotação máxima do evento?',
  'org.step1.audience': 'Quantas pessoas são estimadas no evento?',
  'org.step1.electricity_title': 'Eletricidade',
  'org.step1.electricity_q': 'Seu evento arca com a despesa de energia elétrica?',
  'org.step1.electricity_source_q': 'Qual(s) a(s) fonte(s) de energia do evento?',
  'org.step1.source_grid': 'Rede elétrica da cidade',
  'org.step1.source_generator': 'Gerador',
  'org.step1.source_both': 'Rede elétrica e Gerador',
  'org.step1.grid_consumption': 'Qual o gasto com energia elétrica da rede da cidade?',
  'org.step1.generator_fuel': 'Qual o gasto com combustível para o gerador?',
  'org.step2.title': 'Gastos com deslocamento',
  'org.step2.fuel_internal': 'Combustível para transporte de infraestrutura/materiais',
  'org.step2.fuel_internal_hint': 'Uso interno — assume diesel',
  'org.step2.fuel_employee_private':
    'Deslocamento casa-trabalho dos colaboradores em veículo próprio',
  'org.step2.fuel_employee_private_hint': 'Inclui táxi/Uber. Ida e volta — assume gasolina',
  'org.step2.fuel_employee_public':
    'Deslocamento casa-trabalho dos colaboradores em transporte público',
  'org.step2.fuel_employee_public_hint': 'Ônibus, metrô, etc. Ida e volta. Viag = nº total de viagens',
  'org.step2.consider_audience': 'Deseja considerar as emissões do deslocamento do público?',
  'org.step2.audience_avg_km': 'Distância média percorrida pelo público (ida e volta)',
  'org.step2.pct_public': '% público — transporte público',
  'org.step2.pct_private': '% público — veículo próprio/app',
  'org.step2.pct_walking': '% público — a pé',
  'org.step2.pct_short_flight': '% público — voos curtos (≤ 3h)',
  'org.step2.pct_medium_flight': '% público — voos médios (≤ 6h)',
  'org.step2.pct_long_flight': '% público — voos longos (> 6h)',
  'org.step2.pct_sum_prefix': 'Soma dos percentuais:',
  'org.step2.pct_overflow': 'acima de 100%. Ajuste antes de prosseguir.',
  'org.step2.consider_suppliers':
    'Deseja considerar emissões dos fornecedores (deslocamento de materiais)?',
  'org.step2.suppliers_empty': 'Nenhum fornecedor — clique em "+ Adicionar fornecedor" abaixo.',
  'org.step2.suppliers_add': '+ Adicionar fornecedor',
  'org.step2.supplier_name_placeholder': 'Nome do fornecedor (opcional)',
  'org.step2.supplier_remove_aria': 'Remover fornecedor',
  'org.step3.title': 'Outros',
  'org.step3.duration_hours': 'Quantas horas terá seu evento?',
  'org.step3.duration_hours_hint': 'Multi-day: some todas as horas em atividade',
  'org.step3.glp_kg': 'Gás de cozinha (GLP) para preparação de alimentos',
  'org.step3.waste_organic_kg': 'Lixo orgânico gerado pelo evento',
  'org.step3.waste_recyclable_kg': 'Lixo reciclável gerado pelo evento',
  'org.step4.title': 'Informações do evento',
  'org.step4.intro':
    'Pra finalizar o cálculo e nosso time entrar em contato em até 24h. Dados não são compartilhados.',
  'org.step4.event_name': 'Nome do evento',
  'org.step4.event_name_placeholder': 'Ex.: Minas Summit 2026',
  'org.step4.segment': 'Segmento',
  'org.step4.segment_select': 'Selecione',
  'org.step4.segment_feira': 'Feira',
  'org.step4.segment_festival': 'Festival',
  'org.step4.segment_congresso': 'Congresso',
  'org.step4.segment_corporativo': 'Corporativo',
  'org.step4.segment_outro': 'Outro',
  'org.step4.cpf_cnpj': 'CPF ou CNPJ',
  'org.step4.state': 'Estado',
  'org.step4.city': 'Cidade',
  'org.step4.contact': 'Contato',
  'org.step4.contact_name': 'Nome',
  'org.step4.contact_name_err': 'Informe seu nome.',
  'org.step4.contact_phone': 'Telefone',
  'org.step4.contact_email': 'E-mail',
  'org.step4.contact_email_err': 'Informe um e-mail válido.',
  'org.step4.submit': 'Calcular →',
  'org.step4.submitting': 'Calculando…',

  // Resultado (mockup 15 participante + mockup 20 organização). Strings
  // mudam conforme audience: participante recebe relatório; organização
  // tem contato comercial (lead já capturado no Step 4).
  'result.lead_thanks_participante': '✓ Em breve você receberá seu relatório por e-mail.',
  'result.lead_thanks_org':
    '✓ Recebemos seus dados. Em breve um especialista da OMA entrará em contato.',
};

/** Tipo conveniente reusado pelos step components. */
export type I18nBundle = Record<string, string> | undefined;

/** Resolve `key` no bundle → fallback PT-BR → a própria key (defensivo). */
export function t(bundle: I18nBundle, key: string): string {
  return bundle?.[key] ?? I18N_FALLBACK_PTBR[key] ?? key;
}
